import * as RadixSelect from "@radix-ui/react-select";
import { ReactNode, forwardRef, useId } from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import { Tooltip } from "../Tooltip";
import styles from "./Select.module.scss";

export interface SelectOption {
    /** Stable, unique value used by Radix to identify the option. */
    value: string;
    /** Visible label. Can be a string or a node (icon + text, etc.). */
    label: ReactNode;
    /** Disables the individual option. */
    disabled?: boolean;
}

export interface SelectProps {
    /**
     * Floating label rendered both as the placeholder (centered, 15px) when
     * no value is selected and as a small label (12px) above the value when
     * a value is selected or the menu is open. Required — drives the visual
     * pattern of the Figma `Select` component.
     */
    label: string;
    /** Options to render in the dropdown menu. */
    options: SelectOption[];
    /** Controlled value. */
    value?: string;
    /** Callback when the user selects a value. */
    onValueChange?: (value: string) => void;
    /** Uncontrolled default value. */
    defaultValue?: string;
    /** Helper text rendered below the field. */
    helperText?: string;
    /**
     * Error state. `true` marks the field as invalid; passing a string also
     * sets the helper-text content.
     */
    error?: boolean | string;
    /** Marks as required and appends `*` to the label. */
    required?: boolean;
    /** Disables the trigger. */
    disabled?: boolean;
    /** Stretches to 100% of its container. */
    fullWidth?: boolean;
    /**
     * `compact` = shorter trigger for dense UI (e.g. table footer page size).
     * Hides the floating label and centers the value on one line.
     */
    density?: "default" | "compact";
    /**
     * `field` is the default filled input.
     * `pill` is a brand-green trigger with an optional leading icon
     * in a circle and radio rows in the menu. The label stays
     * screen-reader only.
     */
    appearance?: "field" | "pill";
    /**
     * Name of an `Icon` drawn inside the trigger, before the value.
     * Decorative — the trigger label still names the field.
     * In `pill`, it sits in the darker circle on the left.
     */
    leading?: string;
    /** Shown only when hovering the leading icon, not the rest of the trigger. */
    leadingTooltip?: string;
    /** Small heading at the top of the menu. Uppercased by the pill styles. */
    menuLabel?: string;
    /** Where the menu opens relative to the trigger. */
    side?: "top" | "right" | "bottom" | "left";
    /** How the menu aligns to the trigger. Use `end` when the control sits on the right. */
    align?: "start" | "center" | "end";
    /** Forwarded id (for label association). */
    id?: string;
    /** Forwarded className. */
    className?: string;
    /** Forwarded name (for forms). */
    name?: string;
}

/** Chevron used in the trigger. Rotates 180° when the menu is open. */
const Chevron = () => (
    <span aria-hidden className={styles.chevron}>
        <Icon icon="chevron-down" />
    </span>
);

/** Leading icon. The tooltip, when set, covers only this circle. */
function LeadingIcon({ name, tooltip }: { name: string; tooltip?: string }) {
    const icon = (
        <span aria-hidden className={styles.leading} tabIndex={-1}>
            <Icon icon={name} />
        </span>
    );
    if (!tooltip) return icon;
    return (
        <Tooltip content={tooltip} side="bottom" variant="multi">
            {icon}
        </Tooltip>
    );
}

/**
 * Treez DS — Select (Wave 1).
 *
 * Source: Figma `Select` component (file `qgFv3OQga6RQ4luvSFuGFP`).
 *
 * Composition: `@radix-ui/react-select` (a11y, keyboard nav, portaling)
 * styled to match the `Input` filled visual + `ButtonDropdown` popover
 * shadow for cross-component consistency.
 *
 * Visual pattern:
 * - Closed + no value: bg grey03, label centered 15/24 secondary.
 * - Closed + value:    bg grey03, label up 12/16 secondary, value below.
 * - Open / focused:    bg grey01 + 2px black border on the trigger only
 *                      (same as Input Active). Menu is a separate popover
 *                      below — it does not share the trigger border.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
    function Select(
        {
            label,
            options,
            value,
            onValueChange,
            defaultValue,
            helperText,
            error,
            required,
            disabled,
            fullWidth = false,
            density = "default",
            appearance = "field",
            leading,
            leadingTooltip,
            menuLabel,
            side = "bottom",
            align = "start",
            id,
            className,
            name,
        },
        ref,
    ) {
        const reactId = useId();
        const fieldId = id ?? `ds-select-${reactId}`;
        const messageId = `${fieldId}-msg`;

        const hasError = Boolean(error);
        const message = typeof error === "string" ? error : helperText;
        const isEmpty =
            (value === undefined || value === null || value === "") &&
            (defaultValue === undefined ||
                defaultValue === null ||
                defaultValue === "");
        const isCompact = density === "compact";
        const isPill = appearance === "pill";

        return (
            <RadixSelect.Root
                defaultValue={defaultValue}
                disabled={disabled}
                name={name}
                onValueChange={onValueChange}
                required={required}
                value={value}
            >
                <div
                    className={cx(
                        styles.root,
                        fullWidth && styles.fullWidth,
                        className,
                    )}
                    data-appearance={isPill ? "pill" : undefined}
                    data-density={isCompact ? "compact" : undefined}
                    data-disabled={disabled || undefined}
                    data-empty={isEmpty || undefined}
                    data-error={hasError || undefined}
                    data-ui="select"
                >
                    <RadixSelect.Trigger
                        aria-describedby={message ? messageId : undefined}
                        aria-invalid={hasError || undefined}
                        aria-label={isPill || isCompact ? label : undefined}
                        className={styles.trigger}
                        id={fieldId}
                        ref={ref}
                    >
                        {leading ? (
                            <LeadingIcon
                                name={leading}
                                tooltip={leadingTooltip}
                            />
                        ) : null}
                        <span className={styles.labelValueWrap}>
                            <RadixSelect.Value
                                className={styles.value}
                                placeholder=""
                            />
                            {isPill ? null : (
                                <span
                                    className={
                                        isCompact ? styles.srOnly : styles.label
                                    }
                                >
                                    {label}
                                    {required && " *"}
                                </span>
                            )}
                        </span>
                        <RadixSelect.Icon className={styles.icon}>
                            <Chevron />
                        </RadixSelect.Icon>
                    </RadixSelect.Trigger>

                    <RadixSelect.Portal>
                        <RadixSelect.Content
                            align={align}
                            className={cx(
                                styles.content,
                                isPill && styles.contentPill,
                            )}
                            position="popper"
                            side={side}
                            sideOffset={8}
                        >
                            <RadixSelect.Viewport className={styles.viewport}>
                                <RadixSelect.Group>
                                    {menuLabel ? (
                                        <RadixSelect.Label
                                            className={styles.menuLabel}
                                        >
                                            {menuLabel}
                                        </RadixSelect.Label>
                                    ) : null}
                                    {options.map(opt => (
                                        <RadixSelect.Item
                                            className={cx(
                                                styles.item,
                                                isPill && styles.itemPill,
                                            )}
                                            disabled={opt.disabled}
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {isPill ? (
                                                <span
                                                    aria-hidden
                                                    className={styles.radio}
                                                />
                                            ) : null}
                                            <RadixSelect.ItemText
                                                className={styles.itemText}
                                            >
                                                {opt.label}
                                            </RadixSelect.ItemText>
                                            {isPill ? (
                                                <RadixSelect.ItemIndicator
                                                    className={styles.itemCheck}
                                                >
                                                    <Icon icon="check" />
                                                </RadixSelect.ItemIndicator>
                                            ) : null}
                                        </RadixSelect.Item>
                                    ))}
                                </RadixSelect.Group>
                            </RadixSelect.Viewport>
                        </RadixSelect.Content>
                    </RadixSelect.Portal>

                    {message && (
                        <div className={styles.helper} id={messageId}>
                            {message}
                        </div>
                    )}
                </div>
            </RadixSelect.Root>
        );
    },
);

Select.displayName = "Select";
